// filepath: src/firebase/firestore/useIcrSubscription.js
import {useEffect, useState} from 'react';

import {loadIcrList} from './icr';

/**
 * Fetch the ICR collection ordered by expirationDate ASC.
 *
 * Uses the shared, request-coalescing ICR list cache in `icr.js`, so this hook
 * and per-row draft/fintel enrichment share a single network request per page
 * load instead of firing independent limit=500 / limit=1000 calls.
 *
 * Pass `skip: true` to disable the fetch (e.g. when the data is supplied from a
 * parent via props to avoid duplicate requests). `limit` caps the number of
 * returned rows client-side.
 * Returns { data, loading, error }.
 */
export function useIcrSubscription(options = {}) {
    const {limit = undefined, skip = false} = options;
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(!skip);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (skip) {
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        loadIcrList()
            .then((list) => {
                if (cancelled) return;
                const sorted = Array.isArray(list)
                    ? [...list].sort((a, b) => (a.expirationDate || '') < (b.expirationDate || '') ? -1 : 1)
                    : [];
                const capped = typeof limit === 'number' && limit > 0 ? sorted.slice(0, limit) : sorted;
                setData(capped);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err);
                setLoading(false);
            });

        return () => { cancelled = true; };
    }, [limit, skip]);

    return {data, loading, error};
}
